/**
 * The brand layer at runtime — one store colour in, twelve `--brand-*` values out.
 *
 * `DESIGN_SYSTEM_WEB.md §5` describes the brand layer as a **build artefact**:
 * a delivery copies `themes/example-brand.css`, hand-picks six hexes, imports it
 * after `tokens.css`, ships. That still works and is still the right shape for a
 * white-label build. What it cannot do is give two stores on the same bundle two
 * different colours, which is what this module adds: the same variables, derived
 * from one hex the store chose, written onto `document.documentElement` at
 * sign-in.
 *
 * **The runtime layer wins by construction, not by ordering.** An inline style
 * on the root element beats any `:root` rule in any stylesheet, so a build that
 * ships a theme file *and* signs into a store with a colour gets the store's —
 * and a store with no colour gets its `--brand-*` variables removed rather than
 * blanked, which hands the question back to the theme file, and failing that to
 * the monochrome fallbacks baked into `tokens.css`.
 *
 * Nothing here knows what an order is, and nothing imports it that a primitive
 * could not: this is `lib/`, the same tier as `currency.ts`.
 */

/** `#RRGGBB`. The long form only — a colour input never emits the short one. */
const HEX_RE = /^#[0-9a-f]{6}$/iu

/**
 * The two labels the accent is ever drawn in.
 *
 * A store still does not choose the foreground — it picks the accent and this
 * module picks whichever of these two reads better on it. Pinning white was the
 * earlier rule and it cost every light colour: `#F5C400`, the yellow preset, is
 * 1.64:1 under white and 11.49:1 under ink, so refusing it was refusing a colour
 * that is perfectly readable with the label the till would actually paint.
 * `.docs/ACCENT_THEME_DESIGN.md` §1 is the decision; the server half is
 * `ACCENT_FOREGROUND_LIGHT` / `_INK` in `stores/validators.py`, and the two
 * sides must agree — `accent.test.ts` holds a copy of the server's own table.
 */
export const ACCENT_FOREGROUND_LIGHT = '#FFFFFF'
export const ACCENT_FOREGROUND_INK = '#111111'

/** WCAG 2.1 AA for normal text. Same floor the backend enforces (§8.3). */
export const MIN_ACCENT_CONTRAST = 4.5

/**
 * The page the accent is painted on — `--color-background`, and light-mode only.
 *
 * The same hex as `ACCENT_FOREGROUND_LIGHT` and deliberately not the same
 * constant: one is a label, the other is the ground behind every card, and a
 * rename that moved one would silently move the other. `APP_BACKGROUND` in
 * `stores/validators.py` draws the same distinction.
 */
const APP_BACKGROUND = '#FFFFFF'

/**
 * How far the accent must sit from the page it is drawn on.
 *
 * `--color-background` is `#FFFFFF`, so an accent at 1.05:1 against white is a
 * CTA with no visible edge — legible text floating on the page. The floor is
 * low on purpose: it refuses near-whites and nothing else. `#F5C400` clears it
 * at 1.64 and `#FFD400` at 1.43, which is the point — the yellow end of the
 * space is usable, the white corner of it is not.
 */
export const MIN_BACKGROUND_CONTRAST = 1.3

/**
 * The floor the *disabled* label keeps, and the only floor it keeps.
 *
 * WCAG's 3:1 for large/incidental text rather than 4.5 — a disabled control is
 * deliberately quieter than an enabled one, and a disabled label that matched
 * the active label's contrast would advertise the button nobody can press.
 */
export const MIN_DISABLED_CONTRAST = 3

export type Rgb = { r: number; g: number; b: number }
export type Hsl = { h: number; s: number; l: number }

export function isHexColor(value: string): boolean {
  return HEX_RE.test(value.trim())
}

/**
 * Whatever a human typed, as `#RRGGBB` upper-case — or `null`.
 *
 * Tolerant of a missing `#` because the hex box next to a colour swatch is
 * typed into by hand, and `1C4ED8` is what a person copies out of a brand guide.
 */
export function normalizeHexColor(value: string): string | null {
  const trimmed = value.trim()
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
  return isHexColor(withHash) ? withHash.toUpperCase() : null
}

export function hexToRgb(hex: string): Rgb {
  const raw = hex.replace('#', '')
  return {
    r: Number.parseInt(raw.slice(0, 2), 16),
    g: Number.parseInt(raw.slice(2, 4), 16),
    b: Number.parseInt(raw.slice(4, 6), 16),
  }
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, '0')
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase()
}

/* -------------------------------------------------------------------------- */
/* Contrast — the same maths the backend runs, for the same reason             */
/* -------------------------------------------------------------------------- */

/**
 * WCAG relative luminance, 0 (black) → 1 (white).
 *
 * Duplicated across the boundary on purpose. The server owns the *rule* — it is
 * what actually refuses a write — and this copy owns the *warning*: telling
 * somebody their colour is unreadable while they are still dragging the picker
 * is worth more than telling them after a round trip, and it cannot be done
 * server-side.
 */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex)
  const linear = (channel: number) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
}

/** WCAG contrast ratio between two `#RRGGBB` colours — 1 … 21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const [lighter, darker] = la >= lb ? [la, lb] : [lb, la]
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * The label this accent will actually be drawn in.
 *
 * Whichever of the two reads better, **ties going to white** so the near-black
 * default keeps the label it has always had. Chosen once, off the base accent,
 * and never re-chosen for a derived state: a button whose text flipped from
 * white to black under a finger would be a second colour nobody asked for.
 */
export function accentForeground(hex: string): string {
  return contrastRatio(ACCENT_FOREGROUND_LIGHT, hex) >= contrastRatio(ACCENT_FOREGROUND_INK, hex)
    ? ACCENT_FOREGROUND_LIGHT
    : ACCENT_FOREGROUND_INK
}

/** How well the CTA label reads on this accent — `max(C(white), C(ink))`. */
export function accentContrast(hex: string): number {
  return contrastRatio(accentForeground(hex), hex)
}

/**
 * How well the label reads *while a finger is on the button*.
 *
 * Measured against the foreground the **accent** chose, not one re-picked for
 * the pressed colour — see `accentForeground`.
 */
export function activeAccentContrast(hex: string): number {
  return contrastRatio(accentForeground(hex), deriveAccentPalette(hex).accentActive)
}

/**
 * Why an accent cannot be stored — one of three named rules, or `null`.
 *
 * Named rather than boolean because the card has to say *which way to move*.
 * "Too pale" and "too close to white" send an operator in opposite directions,
 * and a single "bad colour" message sends them nowhere.
 */
export type AccentRejection = 'insufficientContrast' | 'invisiblePress' | 'indistinctFromBackground'

/**
 * The gate — **three checks, all on the accent itself** (design §2).
 *
 * It was one check while the label was pinned to white. Allowing the ink label
 * opened the light half of the space, and the light half does not behave like
 * the dark half:
 *
 *  1. **The label reads.** `max(C(white), C(ink)) ≥ 4.5`. This refuses only the
 *     narrow mid-tone band neither label survives — `#777777`…`#7B7B7B` in
 *     grey, and the matching band at every hue.
 *  2. **The press is visible.** At high saturation *and* high lightness two
 *     sRGB channels are already clamped at 255, so moving HSL lightness moves
 *     almost no luminance: `#FFFF33` presses to `#FFFF0A`, a change of 0.13 L\*
 *     against a floor of 3.5. §3.5 makes that fatal rather than cosmetic — the
 *     `active:` state is the only feedback a tap gets, and a tap that does not
 *     visibly land gets tapped again, which on a POS is a duplicate order. So
 *     the accent must have *some* eligible press worth 3.5 L\*, and
 *     `pressDirection` then picks the largest one, which is that one or better.
 *  3. **The accent is not the page.** `C(accent, #FFFFFF) ≥ 1.3`. Legible ink
 *     on a near-white accent is a CTA with no edge.
 *
 * The rules are checked in that order, and the order is the message: a colour
 * that fails more than one always reports the same reason, and the first is
 * always the more surprising. `#FFFF33` fails 2 and 3 both and is reported as 2,
 * because "your button will not look pressed" is the half a store cannot work
 * out by looking at the swatch.
 *
 * The three names are the API's error codes in camel case
 * (`insufficient_contrast`, `invisible_press`, `indistinct_from_background`), so
 * a rule cannot be renamed on one side of the wire only.
 */
export function accentRejection(hex: string): AccentRejection | null {
  if (accentContrast(hex) < MIN_ACCENT_CONTRAST) return 'insufficientContrast'
  if (!hasVisiblePress(hex)) return 'invisiblePress'
  if (backgroundContrast(hex) < MIN_BACKGROUND_CONTRAST) return 'indistinctFromBackground'
  return null
}

/**
 * Gate 2 — is there a pressed state a thumb can actually see?
 *
 * A candidate counts only if it does **both jobs at once**: keeps the accent's
 * own label above 4.5:1 *and* moves at least `MIN_PRESS_CHANGE` in L\*. One of
 * the two is not enough, which is why this is a `some(… && …)` over candidates
 * rather than two separate checks — a colour with a legible press in one
 * direction and a visible press in the other has neither, because only one
 * direction is ever taken.
 *
 * Only the *active* candidates are considered. Hover moves by the smaller
 * `HOVER_LIFT` in the same direction, so a hover that cleared the floor would
 * imply an active state that cleared it by more. Hover can never be the
 * candidate that rescues a colour, and including it would only make the rule
 * read as though it might.
 */
export function hasVisiblePress(hex: string): boolean {
  const foreground = accentForeground(hex)
  return pressCandidates(hex).some(
    ({ color }) =>
      contrastRatio(foreground, color) >= MIN_ACCENT_CONTRAST &&
      pressChange(hex, color) >= MIN_PRESS_CHANGE,
  )
}

/** How far this accent stands off the page it is painted on. */
export function backgroundContrast(hex: string): number {
  return contrastRatio(hex, APP_BACKGROUND)
}

/** `accentRejection(hex) === null`, for the callers that only need the verdict. */
export function isStorableAccent(hex: string): boolean {
  return accentRejection(hex) === null
}

/**
 * CIE L\* — perceptual lightness, 0 (black) to 100 (white).
 *
 * Used to measure how big a change the pressed state is. WCAG contrast ratio is
 * the wrong tool for that job: it is a ratio of luminances plus a constant, so
 * two near-blacks always score near 1.0 however different they look. `#000003`
 * → `#000047` is an obvious change on screen and 1.09:1 by contrast ratio;
 * L\* puts it at 4.1, which is what the eye reports.
 */
export function perceptualLightness(hex: string): number {
  const y = relativeLuminance(hex)
  const d = 6 / 29
  const f = y > d ** 3 ? Math.cbrt(y) : y / (3 * d * d) + 4 / 29
  return 116 * f - 16
}

/** How big a change the press is, in L\* points. */
export function pressChange(from: string, to: string): number {
  return Math.abs(perceptualLightness(to) - perceptualLightness(from))
}

/**
 * The smallest press this system considers visible.
 *
 * §3.5: `-webkit-tap-highlight-color` is transparent app-wide, so the `active:`
 * state is the *only* feedback a tap gets, and a tap that does not visibly land
 * gets tapped again — on a POS the second tap is a duplicate order.
 *
 * It used to be a guarantee with room under it: while the label was pinned to
 * white the worst press in the whole eligible space was 3.73 L\*. Admitting the
 * ink label admitted the saturated, light corner of sRGB, where two channels are
 * already clamped at 255 and HSL lightness moves almost no luminance — so this
 * is now a **gate** (`accentRejection` rule 2) rather than a property, and
 * `accent.test.ts` sweeps the space to show it holds everywhere the gate lets a
 * colour through.
 */
export const MIN_PRESS_CHANGE = 3.5

/* -------------------------------------------------------------------------- */
/* HSL, and the derivation rules                                               */
/* -------------------------------------------------------------------------- */

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  const d = max - min

  if (d === 0) return { h: 0, s: 0, l }

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  const h =
    max === rn
      ? ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6
      : max === gn
        ? ((bn - rn) / d + 2) / 6
        : ((rn - gn) / d + 4) / 6

  return { h, s, l }
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  if (s === 0) {
    const value = l * 255
    return { r: value, g: value, b: value }
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const channel = (t: number) => {
    const shifted = t < 0 ? t + 1 : t > 1 ? t - 1 : t
    if (shifted < 1 / 6) return p + (q - p) * 6 * shifted
    if (shifted < 1 / 2) return q
    if (shifted < 2 / 3) return p + (q - p) * (2 / 3 - shifted) * 6
    return p
  }
  return { r: channel(h + 1 / 3) * 255, g: channel(h) * 255, b: channel(h - 1 / 3) * 255 }
}

/**
 * Move a lightness by a fraction of the headroom it has left in one direction.
 *
 * A *fraction of headroom* rather than a fixed number of points, and that is
 * the reason the numbers look like this: a fixed ±4.5pp is a visible step on a
 * mid-tone blue and almost nothing on a near-black, so a store that picked a
 * very dark accent would get a pressed state nobody can see. Measured against
 * the two palettes the spec already ships — `themes/example-brand.css`
 * (`#1c4ed8` → `#2a5ce0` → `#3467e6`) and the monochrome defaults in
 * `tokens.css` (`#111111` → `#292929` → `#2e2e2e`) — the same two fractions fit
 * both to within a couple of steps per channel, which a fixed delta does not.
 */
function shift(l: number, fraction: number, direction: PressDirection): number {
  return direction === 'lighter' ? l + fraction * (1 - l) : l - fraction * l
}

/** Hover: one step toward the press. */
const HOVER_LIFT = 0.1
/** Active: one step further — pressed must be distinguishable from hovered. */
const ACTIVE_LIFT = 0.135

/** Disabled always washes out toward white, whichever way the press goes. */
function lift(l: number, fraction: number): number {
  return l + fraction * (1 - l)
}

export type PressDirection = 'lighter' | 'darker'

/**
 * Which way a press moves — **and this is the rule that makes the gate one
 * check instead of two.**
 *
 * Pressing used to always lighten, which is right for the ink-black default and
 * wrong for anything mid-tone: lightening `#767676` (4.54:1, legible) produced
 * `#888888` at 3.54:1, so the white label thinned out at exactly the moment an
 * order was being confirmed. Gating that away would have cost every mid-tone
 * brand colour — `#0F766E`, `#0369A1`, `#166534` all die at a two-sided gate —
 * to fix a problem that is really about *which direction* the press moves.
 *
 * So the direction is chosen per colour, and chosen deterministically rather
 * than from a hard-coded lightness threshold:
 *
 *  1. Compute both candidate pressed colours — one lighter, one darker, each a
 *     fraction of its own headroom.
 *  2. Keep the ones the **accent's own** label still reads on
 *     (`legiblePressCandidates`).
 *  3. Of those, take the one that changes the most, measured in L\*.
 *
 * Step 2 can never empty the set: darkening always raises contrast against
 * white and lightening always raises it against ink, so a white-labelled accent
 * always keeps its darker candidate and an ink-labelled one always keeps its
 * lighter candidate. That is why *readability* under the finger needs no gate —
 * it is guaranteed by construction, and `accent.test.ts` sweeps the space to
 * prove it. *Visibility* is a different claim and does need one, which is rule 2
 * of `accentRejection`.
 *
 * Step 3 is why near-black accents still lighten: `#111111`'s darker candidate
 * is almost itself, so the lighter one wins on change and the monochrome
 * default keeps the `#292929` hover it has always had.
 *
 * The `'darker'` fallback is unreachable for a colour that passes the gate and
 * exists so the function is total.
 */
export function pressDirection(hex: string): PressDirection {
  const candidates = legiblePressCandidates(hex)
  if (candidates.length === 0) return 'darker'

  return candidates.reduce((best, candidate) =>
    pressChange(hex, candidate.color) > pressChange(hex, best.color) ? candidate : best,
  ).direction
}

/**
 * The two pressed colours this accent could reach, lighter and darker.
 *
 * Both of them, unjudged — `hasVisiblePress` and `pressDirection` want different
 * things from the pair and each says so itself, which is what keeps gate 2's
 * "one candidate, both floors" readable as one expression.
 */
function pressCandidates(hex: string): { direction: PressDirection; color: string }[] {
  const { h, s, l } = rgbToHsl(hexToRgb(hex))
  const at = (lightness: number) =>
    rgbToHex(hslToRgb({ h, s, l: Math.min(1, Math.max(0, lightness)) }))

  return [
    { direction: 'lighter', color: at(shift(l, ACTIVE_LIFT, 'lighter')) },
    { direction: 'darker', color: at(shift(l, ACTIVE_LIFT, 'darker')) },
  ]
}

/**
 * The pressed colours the accent's label still reads on — never empty for a
 * colour that clears gate 1.
 *
 * Eligibility is `contrastRatio(foreground, candidate)` and **not**
 * `accentContrast(candidate)`: the latter re-picks the label per candidate, so
 * it would call a pressed state readable in a colour the button is not wearing.
 * `stores/validators.py` makes the same distinction for the same reason.
 */
function legiblePressCandidates(hex: string): { direction: PressDirection; color: string }[] {
  const foreground = accentForeground(hex)
  return pressCandidates(hex).filter(
    ({ color }) => contrastRatio(foreground, color) >= MIN_ACCENT_CONTRAST,
  )
}
/** Disabled: halfway to white, and desaturated so it reads as *off*, not *pale*. */
const DISABLED_LIFT = 0.5
const DISABLED_SATURATION = 0.55
/**
 * Wash: a fixed near-white lightness, hue and saturation untouched. Fixed
 * rather than derived because this is a *background* behind body text — its job
 * is to be the faintest possible tint of the brand at every accent, and
 * anchoring it to the accent's own lightness would make a pale accent's wash
 * indistinguishable from `--color-surface`.
 */
const WASH_LIGHTNESS = 0.96
/**
 * Ink: the app-bar band (`--color-accent-ink`). The brand hue *stains the ink*
 * rather than replacing it — hue and saturation are the store's, lightness is
 * clamped near black. Unlike the wash this is a **ceiling, not a fixed value**:
 * it only ever darkens. This is the *background under white operational text*,
 * and the clamp is what carries that text past 8.4:1 under counter glare no
 * matter what colour a store picks — `#0F766E` itself is only 5.5:1 against
 * white, below the §3.1 floor. (8.4 and not the 9 an earlier comment claimed:
 * a full sweep bottoms out at 8.428, on `#525206`, and `accent.test.ts` pins
 * that number rather than the aspiration.) An accent already at or below the
 * clamp needs no darkening and gets none — see `accentInk` in
 * `deriveAccentPalette`.
 */
const INK_LIGHTNESS = 0.17
const INK_SATURATION_CAP = 0.85

/**
 * The neutral surfaces, stained by the store's hue — design §1, "중성 표면
 * 물들이기: 엷게 고정".
 *
 * **Lightness is the token's own, saturation is capped and injected.** Each
 * entry below is the exact lightness of the monochrome hex it replaces
 * (`#F5F5F5` = 245/255, `#F0F0F0` = 240/255, `#EEEEEE` = 238/255, `#E5E5E5` =
 * 229/255), which is what makes a zero-saturation accent reproduce `tokens.css`
 * byte for byte rather than landing a step off it. The design doc rounds the
 * same four numbers to 0.96 / 0.94 / 0.93 / 0.90.
 *
 * `Math.min(accent.s, cap)` rather than the cap outright, for the reason
 * `accentInk` already uses it: a greyscale accent must not acquire a hue on the
 * way to its surfaces, and a nearly-grey one must not become *more* coloured
 * than the colour it came from.
 *
 * `--color-background` stays pure white and the six greys stay achromatic. What
 * is tinted is the ground between them, and only that.
 */
const TINTED_SURFACES = {
  surface: { lightness: 245 / 255, saturation: 0.06 },
  surfaceVariant: { lightness: 240 / 255, saturation: 0.08 },
  divider: { lightness: 238 / 255, saturation: 0.06 },
  border: { lightness: 229 / 255, saturation: 0.08 },
} as const

export type AccentPalette = {
  accent: string
  accentFg: string
  accentHover: string
  accentActive: string
  accentDisabled: string
  accentDisabledFg: string
  accentWash: string
  accentInk: string
  surface: string
  surfaceVariant: string
  divider: string
  border: string
}

/**
 * The label on a disabled accent — **and it is not always `accentFg`.**
 *
 * `accentDisabled` washes the accent 50% of the way to white while the press
 * only moves it 13.5%, so on a light, ink-labelled accent the disabled button
 * ends up with *more* contrast than the pressed one: `#F5C400` reads 12.6:1
 * disabled against 8.6:1 active. A disabled control that is the loudest thing on
 * the screen advertises the one button nobody can press.
 *
 * So the rule is "as quiet as the floor allows":
 *
 *  - Keep `accentFg` when it already sits in the window — at or above 3:1, and
 *    below what the active state carries. That is the near-black case, and it
 *    is why the monochrome build still draws white on `#8E8E8E` exactly as it
 *    did before this rule existed.
 *  - Otherwise take the neutral grey with the *least* contrast that still
 *    clears 3:1. Solved rather than searched, so it costs no sweep: invert the
 *    WCAG ratio for the luminance each side needs, convert back to a channel,
 *    then step one unit to land on the correct side of the rounding.
 *
 * Both branches land in the `fg-secondary`…`fg-muted` band the design names
 * (`#535353`…`#767676` across the four presets), and the result is always below
 * the active contrast: the searched grey is ~3.0:1 and the active state is
 * ≥4.5:1 by the gate.
 */
function disabledForeground(foreground: string, disabled: string, activeContrast: number): string {
  const plain = contrastRatio(foreground, disabled)
  if (plain >= MIN_DISABLED_CONTRAST && plain < activeContrast) return foreground

  const background = relativeLuminance(disabled)
  const candidates = [
    greyAtOrBelow((background + 0.05) / MIN_DISABLED_CONTRAST - 0.05, disabled),
    greyAtOrAbove(MIN_DISABLED_CONTRAST * (background + 0.05) - 0.05, disabled),
  ].filter((grey): grey is string => grey !== null)

  if (candidates.length === 0) return foreground
  return candidates.reduce((best, grey) =>
    contrastRatio(grey, disabled) < contrastRatio(best, disabled) ? grey : best,
  )
}

/** The sRGB channel value whose linearised luminance is `y`. */
function channelForLuminance(y: number): number {
  return (y <= 0.0031308 ? y * 12.92 : 1.055 * y ** (1 / 2.4) - 0.055) * 255
}

const greyHex = (channel: number) => rgbToHex({ r: channel, g: channel, b: channel })

/** The lightest grey no brighter than `target` luminance that still clears 3:1. */
function greyAtOrBelow(target: number, against: string): string | null {
  if (target < 0) return null
  let channel = Math.min(255, Math.floor(channelForLuminance(target)))
  while (channel >= 0 && contrastRatio(greyHex(channel), against) < MIN_DISABLED_CONTRAST) {
    channel -= 1
  }
  return channel < 0 ? null : greyHex(channel)
}

/** The darkest grey no dimmer than `target` luminance that still clears 3:1. */
function greyAtOrAbove(target: number, against: string): string | null {
  if (target > 1) return null
  let channel = Math.max(0, Math.ceil(channelForLuminance(target)))
  while (channel <= 255 && contrastRatio(greyHex(channel), against) < MIN_DISABLED_CONTRAST) {
    channel += 1
  }
  return channel > 255 ? null : greyHex(channel)
}

/**
 * The `--brand-*` values, from one accent.
 *
 * Hue is never touched. A store picked a colour; a derived state that drifts
 * around the wheel is a second colour they did not pick.
 *
 * Hover and active share one direction — decided once, by `pressDirection`, off
 * the *active* candidates. Deciding them independently could put hover on the
 * far side of the accent from the press, which would read as the button
 * flickering rather than depressing. Sharing it also puts hover strictly
 * between the accent and the press, so a hover state can never fail a floor the
 * press already cleared.
 */
export function deriveAccentPalette(hex: string): AccentPalette {
  const base = rgbToHsl(hexToRgb(hex))
  const at = (s: number, l: number) =>
    rgbToHex(hslToRgb({ h: base.h, s, l: Math.min(1, Math.max(0, l)) }))
  const tinted = (key: keyof typeof TINTED_SURFACES) =>
    at(Math.min(base.s, TINTED_SURFACES[key].saturation), TINTED_SURFACES[key].lightness)

  const direction = pressDirection(hex)
  const accentFg = accentForeground(hex)
  const accentActive = at(base.s, shift(base.l, ACTIVE_LIFT, direction))
  const accentDisabled = at(base.s * DISABLED_SATURATION, lift(base.l, DISABLED_LIFT))

  return {
    accent: hex.toUpperCase(),
    accentFg,
    accentHover: at(base.s, shift(base.l, HOVER_LIFT, direction)),
    accentActive,
    accentDisabled,
    accentDisabledFg: disabledForeground(
      accentFg,
      accentDisabled,
      contrastRatio(accentFg, accentActive),
    ),
    accentWash: at(base.s, WASH_LIGHTNESS),
    // `Math.min` and not `INK_LIGHTNESS` outright: the clamp darkens a light
    // accent into a band white text survives, and must never *lighten* a dark
    // one. Fixed at 0.17 it turned `#111111` into a band of `#2B2B2B`, which
    // repainted the black preset's app bar a shade lighter than the very same
    // bar on a store with no colour set at all — design §1 promises those two
    // are identical, and at saturation 0 the round trip through HSL is exact,
    // so this is a promise about bytes.
    //
    // The saturation cap stays on both sides of the clamp. Handing back the
    // accent whole would be the shorter rule, but a dark accent above the cap
    // (`#555000`) then paints a band 8.288:1 under white, below the 8.428:1
    // floor every other colour keeps. Capping on both sides costs nothing: the
    // black preset still comes back byte-identical, because its saturation is
    // zero and there is nothing for the cap to do.
    accentInk: at(Math.min(base.s, INK_SATURATION_CAP), Math.min(base.l, INK_LIGHTNESS)),
    surface: tinted('surface'),
    surfaceVariant: tinted('surfaceVariant'),
    divider: tinted('divider'),
    border: tinted('border'),
  }
}

/**
 * The four colours the settings card offers as a tap instead of a pick.
 *
 * **Four hexes, not four palettes.** Each one goes through
 * `deriveAccentPalette` like any colour a manager types, so there is one
 * derivation to reason about and a preset can never drift from the free picker
 * (design §1). They are ordered from the house default outward, and every one of
 * them clears all three gates — `accent.test.ts` asserts that rather than
 * trusting it, because a shipped preset the server would refuse is the worst
 * failure this card has.
 *
 * `key` is what the copy is keyed by; the label itself is in `strings.ts`,
 * because `lib/` does not speak Vietnamese.
 */
export const ACCENT_PRESETS = [
  { key: 'black', color: '#111111' },
  { key: 'blue', color: '#1D4ED8' },
  { key: 'green', color: '#00753D' },
  { key: 'yellow', color: '#F5C400' },
] as const

export type AccentPresetKey = (typeof ACCENT_PRESETS)[number]['key']

/* -------------------------------------------------------------------------- */
/* The DOM half                                                                */
/* -------------------------------------------------------------------------- */

/** Property name per palette entry. The contract with `tokens.css` §5.2. */
const BRAND_VARIABLES: Record<keyof AccentPalette, string> = {
  accent: '--brand-accent',
  accentFg: '--brand-accent-fg',
  accentHover: '--brand-accent-hover',
  accentActive: '--brand-accent-active',
  accentDisabled: '--brand-accent-disabled',
  accentDisabledFg: '--brand-accent-disabled-fg',
  accentWash: '--brand-accent-wash',
  accentInk: '--brand-accent-ink',
  surface: '--brand-surface',
  surfaceVariant: '--brand-surface-variant',
  divider: '--brand-divider',
  border: '--brand-border',
}

/**
 * Paint the brand layer, or take it away.
 *
 * `null`, an unparseable value, **or a colour that fails any of the three
 * gates** all mean the same thing: remove the variables and fall back to
 * monochrome. The last of those is the interesting one — the server already
 * refuses to store such a colour, so reaching it means a row that predates the
 * rule or one written straight into the database, and an invisible "Nhận đơn"
 * is a worse failure than an unbranded till.
 *
 * Returns what was actually applied, which is `null` whenever it fell back.
 */
export function applyAccentColor(hex: string | null | undefined): AccentPalette | null {
  const root = document.documentElement
  const normalized = hex == null ? null : normalizeHexColor(hex)

  if (normalized === null || !isStorableAccent(normalized)) {
    clearAccentColor()
    return null
  }

  const palette = deriveAccentPalette(normalized)
  for (const [key, property] of Object.entries(BRAND_VARIABLES)) {
    root.style.setProperty(property, palette[key as keyof AccentPalette])
  }
  return palette
}

/**
 * Back to monochrome.
 *
 * `removeProperty`, never `setProperty(…, '#111111')`: writing the default hex
 * would out-rank a `themes/<brand>.css` a delivery had shipped, so signing out
 * of a store would silently un-brand a white-label build. Removing hands the
 * question back to the cascade.
 */
export function clearAccentColor(): void {
  const root = document.documentElement
  for (const property of Object.values(BRAND_VARIABLES)) {
    root.style.removeProperty(property)
  }
}
